import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AuthCard from "../../components/AuthCard";
import { signUp, isValidEmail, isValidPassword } from "../../lib/auth";

const SERVICES = [
  { id: "roofing", label: "Roofing" },
  { id: "solar", label: "Solar" },
  { id: "landscaping", label: "Landscaping" },
  { id: "hvac", label: "HVAC" },
  { id: "plumbing", label: "Plumbing" },
  { id: "electrical", label: "Electrical" },
  { id: "kitchen_remodeling", label: "Kitchen Remodeling" },
  { id: "bathroom_remodeling", label: "Bathroom Remodeling" },
  { id: "general_remodeling", label: "General Remodeling" },
];

const PLAN_SUMMARY = {
  starter: {
    name: "Starter",
    price: "$44/mo",
    detail: "Best for testing BuildBright in one focused service area.",
  },
  pro: {
    name: "Pro",
    price: "$89/mo",
    detail: "Best for broader ZIP coverage, SMS alerts, and faster project visibility.",
  },
  territory_king: {
    name: "Territory King",
    price: "$143/mo",
    detail: "Premium tier for established contractors seeking market dominance.",
  },
};

export default function SignUp() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const params = new URLSearchParams(location.search);
  const requestedPlan = params.get("plan") || "starter";
  const selectedPlan = PLAN_SUMMARY[requestedPlan] ? requestedPlan : "starter";
  const selectedPlanDetails = PLAN_SUMMARY[selectedPlan];

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    company: "",
    phone: "",
    location: "",
    services: [],
    acceptTerms: false,
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleServiceToggle = (serviceId) => {
    const updatedServices = formData.services.includes(serviceId)
      ? formData.services.filter((s) => s !== serviceId)
      : [...formData.services, serviceId];

    setFormData({
      ...formData,
      services: updatedServices,
    });
  };

  const handleTermsChange = (e) => {
    setFormData({
      ...formData,
      acceptTerms: e.target.checked,
    });
  };

  const validateForm = () => {
    if (!formData.email || !isValidEmail(formData.email)) {
      setError("Please enter a valid email address");
      return false;
    }

    if (!formData.password || !isValidPassword(formData.password)) {
      setError("Password must be at least 8 characters long");
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return false;
    }

    if (!formData.name.trim()) {
      setError("Full name is required");
      return false;
    }

    if (!formData.company.trim()) {
      setError("Company name is required");
      return false;
    }

    if (!formData.phone.trim()) {
      setError("Phone number is required");
      return false;
    }

    if (!formData.location.trim()) {
      setError("Location is required");
      return false;
    }

    if (formData.services.length === 0) {
      setError("Please select at least one service");
      return false;
    }

    if (!formData.acceptTerms) {
      setError("You must accept the terms and conditions");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const result = await signUp({
        email: formData.email,
        password: formData.password,
        name: formData.name,
        company: formData.company,
        phone: formData.phone,
        location: formData.location,
        services: formData.services,
        accept_terms: formData.acceptTerms,
      });

      setSuccess("Account created! Setting up your subscription...");

      // Auto sign-in after signup
      try {
        const loginRes = await fetch("https://api.buildbright.app/api/contractor/signin", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: formData.email, password: formData.password }),
        });
        const loginData = await loginRes.json();
        if (loginData.token) {
          localStorage.setItem("bb_contractor_token", loginData.token);
        }
        if (loginData.contractor) {
          localStorage.setItem("bb_contractor", JSON.stringify(loginData.contractor));
        }

        // Check for plan param in React Router location search.
        // HashRouter stores ?plan=pro inside the route, not window.location.search.
        const plan = selectedPlan;

        // Launch Stripe checkout
        const checkoutRes = await fetch("https://api.buildbright.app/api/billing/checkout", {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(loginData.token ? { "Authorization": `Bearer ${loginData.token}` } : {})
          },
          body: JSON.stringify({ tier: plan }),
        });
        const checkoutData = await checkoutRes.json().catch(() => ({}));

        if (!checkoutRes.ok) {
          const checkoutMessage =
            typeof checkoutData?.detail === "string"
              ? checkoutData.detail
              : checkoutData?.detail?.message ||
                checkoutData?.detail?.code ||
                checkoutData?.message ||
                "Checkout could not start. Please contact BuildBright support.";

          throw new Error(checkoutMessage);
        }

        if (checkoutData.url) {
          window.location.href = checkoutData.url;
          return;
        }

        throw new Error("Checkout could not start. Please contact BuildBright support.");
      } catch (e) {
        console.error("Auto-checkout failed", e);
        setSuccess("");
        setError(e.message || "Account created, but checkout could not start. Please contact BuildBright support.");
        return;
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <AuthCard
        title="Account Created!"
        description="Check your email to verify your account"
      >
        <div className="text-center">
          <div className="text-green-600 text-6xl mb-4">✓</div>
          <p className="text-sm text-gray-600 mb-6">{success}</p>
          <a
            href="/#/contractors/login"
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
          >
            Go to Sign In
          </a>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Create Your Contractor Account"
      description="Create your account, then continue to secure checkout for your selected plan."
    >
      <div className="mb-6 rounded-xl border border-orange-200 bg-orange-50 p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-orange-700">Selected plan</p>
            <p className="text-lg font-bold text-gray-900">{selectedPlanDetails.name}</p>
            <p className="text-sm text-gray-600">{selectedPlanDetails.detail}</p>
          </div>
          <div className="text-right">
            <p className="text-xl font-bold text-orange-600">{selectedPlanDetails.price}</p>
            <a href="/#/contractors/pricing" className="text-xs text-orange-700 hover:text-orange-800">
              Change plan
            </a>
          </div>
        </div>
      </div>

      <div className="mb-6 rounded-xl border border-gray-200 bg-gray-50 p-4">
        <p className="text-sm font-semibold text-gray-900 mb-2">What happens next</p>
        <ul className="space-y-1 text-sm text-gray-600">
          <li>✓ Create your contractor account</li>
          <li>✓ Continue to secure Stripe checkout</li>
          <li>✓ Verify your email and access the contractor portal</li>
          <li>✓ Review project opportunity previews in your service area</li>
        </ul>
        <p className="mt-3 text-xs text-gray-500">
          BuildBright supports project intake and contractor marketing visibility. Project availability, homeowner response, pricing, and outcomes are not guaranteed.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-300 rounded-md p-3">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Email */}
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700"
          >
            Email Address *
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            value={formData.email}
            onChange={handleInputChange}
            className="mt-1 appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-orange-500 focus:border-orange-500 focus:z-10 sm:text-sm"
            placeholder="contractor@example.com"
          />
        </div>

        {/* Password */}
        <div>
          <label
            htmlFor="password"
            className="block text-sm font-medium text-gray-700"
          >
            Password *
          </label>
          <input
            id="password"
            name="password"
            type="password"
            required
            value={formData.password}
            onChange={handleInputChange}
            className="mt-1 appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-orange-500 focus:border-orange-500 focus:z-10 sm:text-sm"
            placeholder="At least 8 characters"
          />
        </div>

        {/* Confirm Password */}
        <div>
          <label
            htmlFor="confirmPassword"
            className="block text-sm font-medium text-gray-700"
          >
            Confirm Password *
          </label>
          <input
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            required
            value={formData.confirmPassword}
            onChange={handleInputChange}
            className="mt-1 appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-orange-500 focus:border-orange-500 focus:z-10 sm:text-sm"
            placeholder="Confirm your password"
          />
        </div>

        {/* Name */}
        <div>
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700"
          >
            Full Name *
          </label>
          <input
            id="name"
            name="name"
            type="text"
            required
            value={formData.name}
            onChange={handleInputChange}
            className="mt-1 appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-orange-500 focus:border-orange-500 focus:z-10 sm:text-sm"
            placeholder="John Smith"
          />
        </div>

        {/* Company */}
        <div>
          <label
            htmlFor="company"
            className="block text-sm font-medium text-gray-700"
          >
            Company Name *
          </label>
          <input
            id="company"
            name="company"
            type="text"
            required
            value={formData.company}
            onChange={handleInputChange}
            className="mt-1 appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-orange-500 focus:border-orange-500 focus:z-10 sm:text-sm"
            placeholder="ABC Contractors LLC"
          />
        </div>

        {/* Phone */}
        <div>
          <label
            htmlFor="phone"
            className="block text-sm font-medium text-gray-700"
          >
            Phone Number *
          </label>
          <input
            id="phone"
            name="phone"
            type="tel"
            required
            value={formData.phone}
            onChange={handleInputChange}
            className="mt-1 appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-orange-500 focus:border-orange-500 focus:z-10 sm:text-sm"
            placeholder="(555) 123-4567"
          />
        </div>

        {/* Location */}
        <div>
          <label
            htmlFor="location"
            className="block text-sm font-medium text-gray-700"
          >
            Service Location *
          </label>
          <input
            id="location"
            name="location"
            type="text"
            required
            value={formData.location}
            onChange={handleInputChange}
            className="mt-1 appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-orange-500 focus:border-orange-500 focus:z-10 sm:text-sm"
            placeholder="City, State"
          />
        </div>

        {/* Services */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Services Offered * (Select all that apply)
          </label>
          <div className="grid grid-cols-2 gap-3">
            {SERVICES.map((service) => (
              <label key={service.id} className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.services.includes(service.id)}
                  onChange={() => handleServiceToggle(service.id)}
                  className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">
                  {service.label}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Terms */}
        <div>
          <label className="flex items-start">
            <input
              type="checkbox"
              checked={formData.acceptTerms}
              onChange={handleTermsChange}
              className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded mt-1"
            />
            <span className="ml-2 text-sm text-gray-700">
              I agree to the{" "}
              <a
                href="/#/terms"
                target="_blank"
                rel="noreferrer"
                className="text-orange-600 hover:text-orange-500"
              >

                Terms of Service
              </a>{" "}
              and{" "}
              <a
                href="/#/privacy"
                className="text-orange-600 hover:text-orange-500"
              >
                Privacy Policy
              </a>
              . BuildBright will email me a verification link. *
            </span>
          </label>
        </div>

        {/* Submit */}
        <div>
          <button
            type="submit"
            disabled={loading}
            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Creating Account & Starting Checkout..." : "Create Account & Continue to Checkout"}
          </button>
        </div>

        {/* Sign In Link */}
        <div className="text-center">
          <p className="text-sm text-gray-600">
            Already have an account?{" "}
            <a
              href="/#/contractor/sign-in"
              className="font-medium text-orange-600 hover:text-orange-500"
            >
              Sign in
            </a>
          </p>
        </div>
      </form>
    </AuthCard>
  );
}
